import React, { useMemo } from 'react';
import {
  FilterStructure,
  FilterType,
  ReportFilterItem,
} from '../../reportsTypes';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  SelectData,
  SelectWithSearch,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@ais-ui/ui-core';
import { useGetFilters } from '@ais-ui/websockets';
import { useFetchAllShiplists } from '@ais-ui/api';
import { ShipList } from '@ais-ui/api-spec/config';
import { MultiShipCommand } from '../../../utils/ShipCommand';

import { UseFormReturn } from 'react-hook-form';
import { ReportCloneValues } from './CloneReport';
import { useLocalization } from '@ais-ui/locale-management';

interface NormalFilterProps {
  form: UseFormReturn<ReportCloneValues>;
  filterItems: ReportFilterItem[];
}

const NormalFilterForm = ({ form, filterItems }: NormalFilterProps) => {
  const { data: filters } = useGetFilters();

  const selections = useMemo(() => {
    const selectionItems = filters
      ? filters.map((filter) => ({
          id: filter.uuid,
          name: filter.name,
        }))
      : [];
    selectionItems.unshift({ id: 'no-filter', name: 'No filter' });
    return selectionItems;
  }, [filters]);

  return (
    <FormField
      control={form.control}
      name="filter"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Filter:</FormLabel>
          <FormControl onChange={() => form.clearErrors()}>
            <SelectWithSearch
              value={field.value}
              onChange={field.onChange}
              data={selections}
              placeholder="Select a filter"
            ></SelectWithSearch>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

interface ShipFilterProps {
  form: UseFormReturn<ReportCloneValues>;
  filterItems: ReportFilterItem[];
}

const ShipFilterForm = ({ form, filterItems }: ShipFilterProps) => {
  return (
    <FormField
      control={form.control}
      name="filter"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Filter:</FormLabel>
          <FormControl onChange={() => form.clearErrors()}>
            <MultiShipCommand
              value={field.value}
              onChange={field.onChange}
            ></MultiShipCommand>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

interface ShiplistFilterProps {
  form: UseFormReturn<ReportCloneValues>;
  filterItems: ReportFilterItem[];
}

const ShiplistFilterForm = ({ form, filterItems }: ShiplistFilterProps) => {
  const { data: shiplists } = useFetchAllShiplists();

  const selections = useMemo(() => {
    const selectionItems: SelectData[] =
      shiplists && shiplists.records
        ? shiplists.records.map((shiplist: ShipList) => ({
            id: shiplist.id ? shiplist.id : '',
            name: shiplist.name ? shiplist.name : '',
          }))
        : [];
    selectionItems.unshift({ id: 'no-filter', name: 'No filter' });
    return selectionItems;
  }, [shiplists]);

  return (
    <FormField
      control={form.control}
      name="filter"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Filter:</FormLabel>
          <FormControl onChange={() => form.clearErrors()}>
            <SelectWithSearch
              value={field.value}
              onChange={field.onChange}
              data={selections}
              placeholder="Select a shiplist"
            ></SelectWithSearch>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

interface CombinedFilterProps {
  form: UseFormReturn<ReportCloneValues>;
  filterItems: ReportFilterItem[];
}

const CombinedFilterForm = ({ form, filterItems }: CombinedFilterProps) => {
  const { data: filters } = useGetFilters();
  const { messages } = useLocalization();
  const { data: shiplists } = useFetchAllShiplists();

  const shiplistSelections = useMemo(() => {
    const selectionItems: SelectData[] =
      shiplists && shiplists.records
        ? shiplists.records.map((shiplist: ShipList) => ({
            id: shiplist.id ? shiplist.id : '',
            name: shiplist.name ? shiplist.name : '',
          }))
        : [];
    selectionItems.unshift({
      id: 'no-filter',
      name: messages.reportsCreateReportNoFilterValue,
    });
    return selectionItems;
  }, [shiplists]);

  const filterSelections = useMemo(() => {
    const selectionItems = filters
      ? filters.map((filter) => ({
          id: filter.uuid,
          name: filter.name,
        }))
      : [];
    selectionItems.unshift({ id: 'no-filter', name: 'No filter' });
    return selectionItems;
  }, [filters]);

  return (
    <div className="space-y-8">
      {filterItems.map((filterItem) => {
        if (filterItem.name === 'shiplist') {
          return (
            <FormField
              control={form.control}
              name="shiplist"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{filterItem.label}:</FormLabel>
                  <FormControl onChange={() => form.clearErrors()}>
                    <SelectWithSearch
                      value={field.value}
                      onChange={field.onChange}
                      data={shiplistSelections}
                      placeholder={
                        messages.reportsCreateReportSelectShiplistPlaceholder
                      }
                    ></SelectWithSearch>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          );
        }
        if (filterItem.name === 'filter') {
          return (
            <FormField
              control={form.control}
              name="filter"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{filterItem.label}:</FormLabel>
                  <FormControl onChange={() => form.clearErrors()}>
                    <SelectWithSearch
                      value={field.value}
                      onChange={field.onChange}
                      data={filterSelections}
                      placeholder={
                        messages.reportsCreateReportSelectFilterPlaceholder
                      }
                    ></SelectWithSearch>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          );
        }
      })}
    </div>
  );
};

type filterFormFieldProps = {
  form: UseFormReturn<ReportCloneValues>;
  filterFields: FilterStructure;
  filterType: FilterType;
  setFilterType: (filterType: FilterType) => void;
};

interface SingleContentProps {
  form: UseFormReturn<ReportCloneValues>;
  filterFields: FilterStructure;
}

const SingleContent = ({ form, filterFields }: SingleContentProps) => {
  if (
    Object.prototype.hasOwnProperty.call(filterFields, 'normal') &&
    filterFields.normal !== undefined
  ) {
    const filterItems: ReportFilterItem[] = filterFields.normal;
    return <NormalFilterForm form={form} filterItems={filterItems} />;
  } else if (
    Object.prototype.hasOwnProperty.call(filterFields, 'ship') &&
    filterFields.ship !== undefined
  ) {
    const filterItems: ReportFilterItem[] = filterFields.ship;
    return (
      <ShipFilterForm form={form} filterItems={filterItems}></ShipFilterForm>
    );
  } else if (
    Object.prototype.hasOwnProperty.call(filterFields, 'shiplist') &&
    filterFields.shiplist !== undefined
  ) {
    const filterItems: ReportFilterItem[] = filterFields.shiplist;
    return (
      <ShiplistFilterForm
        form={form}
        filterItems={filterItems}
      ></ShiplistFilterForm>
    );
  } else if (
    Object.prototype.hasOwnProperty.call(filterFields, 'combined') &&
    filterFields.combined !== undefined
  ) {
    const filterItems: ReportFilterItem[] = filterFields.combined;
    return (
      <CombinedFilterForm
        form={form}
        filterItems={filterItems}
      ></CombinedFilterForm>
    );
  } else {
    return;
  }
};

interface TabbedContentProps {
  form: UseFormReturn<ReportCloneValues>;
  filterFields: FilterStructure;
  filterType: FilterType;
  setFilterType: (filterType: FilterType) => void;
}

const TabbedContent = ({
  form,
  filterFields,
  filterType,
  setFilterType,
}: TabbedContentProps) => {
  const normalFilterItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(filterFields, 'normal')) {
      return filterFields.normal;
    }
    return null; // or a default value
  }, [filterFields]);

  const shipFilterItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(filterFields, 'ship')) {
      return filterFields.ship;
    }
    return null; // or a default value
  }, [filterFields]);

  const shiplistFilterItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(filterFields, 'shiplist')) {
      return filterFields.shiplist;
    }
    return null; // or a default value
  }, [filterFields]);

  return (
    <Tabs
      value={filterType}
      onValueChange={setFilterType as (filterType: string) => void}
      defaultValue={filterType}
      className="w-full space-y-6 pt-4 h-full flex flex-col"
    >
      <TabsList className="w-fit">
        {normalFilterItems && (
          <TabsTrigger id="normal-filter" value="normal">
            Normal
          </TabsTrigger>
        )}
        {shipFilterItems && (
          <TabsTrigger id="ship-filter" value="ship">
            Ship
          </TabsTrigger>
        )}
        {shiplistFilterItems && (
          <TabsTrigger id="shiplist-filter" value="shiplist">
            Shiplist
          </TabsTrigger>
        )}
      </TabsList>
      {normalFilterItems && filterType === 'normal' && (
        <NormalFilterForm form={form} filterItems={normalFilterItems} />
      )}
      {shipFilterItems && filterType === 'ship' && (
        <ShipFilterForm form={form} filterItems={shipFilterItems} />
      )}
      {shiplistFilterItems && filterType === 'shiplist' && (
        <ShiplistFilterForm form={form} filterItems={shiplistFilterItems} />
      )}
    </Tabs>
  );
};

export const FilterFormField = ({
  form,
  filterFields,
  filterType,
  setFilterType,
}: filterFormFieldProps) => {
  return (
    <>
      {Object.keys(filterFields).length === 1 && (
        <SingleContent form={form} filterFields={filterFields}></SingleContent>
      )}
      {Object.keys(filterFields).length > 1 && (
        <TabbedContent
          form={form}
          filterType={filterType}
          setFilterType={setFilterType}
          filterFields={filterFields}
        ></TabbedContent>
      )}
    </>
  );
};
